import React,{useCallback,useEffect,useRef,useState} from 'react';
import QRCode from 'qrcode';
import {motion} from 'framer-motion';
import {requestPickupToken,redeemPickupToken,reportPickupProblem} from '../../services/pickupService';
import SafeIcon from '../../common/SafeIcon';
import PickupQrScanner from './PickupQrScanner';
import * as FiIcons from 'react-icons/fi';

const {
  FiCheckCircle,
  FiClock,
  FiCopy,
  FiAlertCircle,
  FiRefreshCw,
  FiShield,
  FiSend,
  FiCamera,
  FiKey
}=FiIcons;

const normalizePickupCode=(value='')=>{
  return String(value)
    .replace(/[^a-z0-9]/gi,'')
    .toUpperCase()
    .slice(0,8);
};

const PickupConfirmationPanel=({order,role='buyer',onConfirmed})=>{
  const [token,setToken]=useState(null);
  const [pickupCode,setPickupCode]=useState('');
  const [qrPayload,setQrPayload]=useState('');
  const [qrImage,setQrImage]=useState('');
  const [remaining,setRemaining]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState(false);
  const [copied,setCopied]=useState(false);
  const [problem,setProblem]=useState('');
  const [problemDescription,setProblemDescription]=useState('');
  const [showProblem,setShowProblem]=useState(false);
  const [showScanner,setShowScanner]=useState(false);
  const [verificationMode,setVerificationMode]=useState('qr');
  const requestInFlightRef=useRef(false);
  const autoRequestedOrderRef=useRef(null);
  const isBuyer=role==='buyer';
  const isReady=order?.status==='ready' && order?.delivery_method==='pickup';
  const isExpired=Boolean(
    token?.expires_at &&
    new Date(token.expires_at).getTime()<=Date.now()
  );
  const normalizedCode=normalizePickupCode(pickupCode);
  const canVerify=verificationMode==='qr'
    ? Boolean(qrPayload && !loading)
    : Boolean(normalizedCode.length>=6 && !loading);

  const createToken=useCallback(async()=>{
    if (!order?.id || requestInFlightRef.current) return;

    requestInFlightRef.current=true;
    setLoading(true);
    setError('');
    setToken(null);
    setPickupCode('');
    setCopied(false);
    setQrPayload('');
    setQrImage('');
    setRemaining('');

    try {
      const result=await requestPickupToken(order.id);

      if (
        !result?.qr_payload ||
        !result?.pickup_code ||
        !result?.expires_at
      ) {
        throw new Error(
          'The pickup service returned an incomplete token.'
        );
      }

      const image=await QRCode.toDataURL(
        result.qr_payload,
        {width:280,margin:2,errorCorrectionLevel:'M'}
      );

      setToken(result);
      setPickupCode(result.pickup_code);
      setQrPayload(result.qr_payload);
      setRemaining(formatRemaining(result.expires_at));
      setQrImage(image);
    } catch (requestError) {
      setError(
        requestError?.message ||
        'Unable to create a pickup token.'
      );
    } finally {
      requestInFlightRef.current=false;
      setLoading(false);
    }
  },[order?.id]);

  useEffect(()=>{
    if (!isBuyer || !isReady || token || !order?.id) return;
    if (autoRequestedOrderRef.current===order.id) return;

    autoRequestedOrderRef.current=order.id;
    createToken();
  },[createToken,isBuyer,isReady,order?.id,token]);

  useEffect(()=>{
    if (!token?.expires_at) return undefined;

    const timer=window.setInterval(()=>{
      setRemaining(formatRemaining(token.expires_at));
    },1000);

    return ()=>window.clearInterval(timer);
  },[token?.expires_at]);

  const copyPickupCode=async()=>{
    if (!pickupCode) return;

    setError('');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pickupCode);
      } else {
        const textArea=document.createElement('textarea');
        textArea.value=pickupCode;
        textArea.setAttribute('readonly','');
        textArea.style.position='fixed';
        textArea.style.opacity='0';
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0,textArea.value.length);
        const copiedSuccessfully=document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!copiedSuccessfully) {
          throw new Error('Copy is unavailable in this browser.');
        }
      }

      setCopied(true);
      window.setTimeout(()=>setCopied(false),2000);
    } catch (copyError) {
      setError(
        copyError?.message ||
        'Unable to copy the pickup code. Please select and copy it manually.'
      );
    }
  };

  const redeem=async()=>{
    if (!canVerify) {
      setError(
        verificationMode==='qr'
          ? 'Scan the buyer QR code before verifying.'
          : 'Enter the buyer pickup code before verifying.'
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result=await redeemPickupToken({
        orderId:order.id,
        qrPayload:verificationMode==='qr' ? qrPayload : '',
        pickupCode:verificationMode==='code' ? normalizedCode : '',
        confirmationMethod:verificationMode,
        fallbackUsed:false,
        deviceFingerprint:window.navigator.userAgent
      });

      if (!result?.success) {
        throw new Error(
          'Pickup verification did not return a success response.'
        );
      }

      setSuccess(true);
      onConfirmed?.(result);
    } catch (redeemError) {
      setError(
        redeemError?.message ||
        'Pickup could not be confirmed.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleScan=useCallback((value)=>{
    setQrPayload(value);
    setVerificationMode('qr');
    setShowScanner(false);
    setError('');
  },[]);

  const submitProblem=async(event)=>{
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await reportPickupProblem({
        orderId:order.id,
        problemType:problem,
        description:problemDescription
      });
      setShowProblem(false);
      setProblemDescription('');
      setProblem('');
    } catch (problemError) {
      setError(
        problemError?.message ||
        'Unable to report the pickup problem.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (isBuyer) {
    if (!isReady) return null;

    return (
      <>
        <motion.section
          initial={{opacity:0,y:10}}
          animate={{opacity:1,y:0}}
          className="mt-6 rounded-[2rem] border border-indigo-100 bg-indigo-50/50 p-6"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center text-indigo-700">
                <SafeIcon icon={FiShield} className="mr-2" />
                <h3 className="text-sm font-black uppercase tracking-widest">
                  Buyer Confirmation Required
                </h3>
              </div>
              <p className="mt-2 text-xs font-medium leading-relaxed text-indigo-900/70">
                Show the seller either the one-time QR code or the pickup code.
              </p>
            </div>
            <SafeIcon icon={FiClock} className="text-xl text-indigo-500" />
          </div>

          {error && (
            <div className="mt-5 flex items-start rounded-2xl border border-red-100 bg-red-50 p-4 text-xs font-bold text-red-700">
              <SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5" />
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading && !token ? (
              <div className="flex items-center justify-center rounded-2xl bg-white p-8 text-xs font-black uppercase tracking-widest text-indigo-600">
                <SafeIcon icon={FiRefreshCw} className="mr-3 animate-spin" />
                Generating one-time code
              </div>
            ) : token && !isExpired ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="rounded-2xl bg-white p-5 text-center">
                  {qrImage && (
                    <img
                      src={qrImage}
                      alt="One-time buyer pickup QR code"
                      className="mx-auto h-48 w-48"
                    />
                  )}
                  <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                    QR code expires in {remaining}
                  </p>
                </div>

                <div className="flex flex-col justify-center rounded-2xl bg-white p-5">
                  <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <SafeIcon icon={FiKey} className="mr-2" />
                    One-time Pickup Code
                  </div>
                  <p className="my-3 break-all text-3xl font-black tracking-[0.2em] text-indigo-600">
                    {pickupCode}
                  </p>
                  <button
                    type="button"
                    onClick={copyPickupCode}
                    className="flex items-center justify-center rounded-xl bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                  >
                    <SafeIcon icon={FiCopy} className="mr-2" />
                    {copied ? 'Code Copied' : 'Copy Code'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-white p-6 text-center">
                <p className="text-xs font-bold text-red-600">
                  {error
                    ? 'We could not generate a pickup code.'
                    : 'This pickup code has expired.'}
                </p>
                <button
                  type="button"
                  onClick={createToken}
                  disabled={loading}
                  className="mt-4 flex w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                >
                  <SafeIcon
                    icon={loading ? FiRefreshCw : FiKey}
                    className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                  />
                  Generate New Code and QR
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={()=>setShowProblem(!showProblem)}
            className="mt-5 flex items-center text-[10px] font-black uppercase tracking-widest text-slate-500"
          >
            <SafeIcon icon={FiSend} className="mr-2" />
            Pickup Problem
          </button>

          {showProblem && (
            <form
              onSubmit={submitProblem}
              className="mt-5 space-y-3 rounded-2xl bg-white p-5"
            >
              <select
                required
                value={problem}
                onChange={(event)=>setProblem(event.target.value)}
                className="w-full rounded-xl bg-slate-50 px-4 py-3 text-xs font-bold outline-none"
              >
                <option value="">Select a problem</option>
                <option value="buyer_claims_not_received">
                  Buyer says order was not received
                </option>
                <option value="wrong_person">
                  Wrong person attempting pickup
                </option>
                <option value="missing_items">Missing items</option>
                <option value="damaged_items">
                  Missing or damaged items
                </option>
                <option value="unable_to_verify">
                  Unable to verify pickup
                </option>
                <option value="expired_token">Expired token</option>
                <option value="connectivity_problem">
                  Connectivity problem
                </option>
                <option value="other">Other</option>
              </select>

              <textarea
                required
                value={problemDescription}
                onChange={(event)=>setProblemDescription(event.target.value)}
                rows="3"
                placeholder="Describe what happened"
                className="w-full resize-none rounded-xl bg-slate-50 px-4 py-3 text-xs font-medium outline-none"
              />

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
              >
                Submit Pickup Problem
              </button>
            </form>
          )}
        </motion.section>
      </>
    );
  }

  if (!isReady && !success) return null;

  return (
    <>
      <motion.section
        initial={{opacity:0,y:10}}
        animate={{opacity:1,y:0}}
        className="mt-6 rounded-[2rem] border border-indigo-100 bg-indigo-50/50 p-6"
      >
        {success ? (
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 text-center">
            <SafeIcon
              icon={FiCheckCircle}
              className="mx-auto mb-3 text-4xl text-emerald-600"
            />
            <h3 className="text-lg font-black text-emerald-900">
              Pickup confirmed successfully
            </h3>
            <p className="mt-2 text-xs font-bold text-emerald-700">
              The order has been marked completed.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center text-indigo-700">
                  <SafeIcon icon={FiShield} className="mr-2" />
                  <h3 className="text-sm font-black uppercase tracking-widest">
                    Buyer Confirmation Required
                  </h3>
                </div>
                <p className="mt-2 text-xs font-medium leading-relaxed text-indigo-900/70">
                  Verify the handoff using either the buyer QR code or the separate pickup code.
                </p>
              </div>
              <SafeIcon icon={FiClock} className="text-xl text-indigo-500" />
            </div>

            {error && (
              <div className="mt-5 flex items-start rounded-2xl border border-red-100 bg-red-50 p-4 text-xs font-bold text-red-700">
                <SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5" />
                {error}
              </div>
            )}

            <div className="mt-6 flex rounded-2xl bg-white p-1">
              <button
                type="button"
                onClick={()=>{
                  setVerificationMode('qr');
                  setError('');
                }}
                className={`flex-1 rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
                  verificationMode==='qr'
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400'
                }`}
              >
                Scan QR
              </button>

              <button
                type="button"
                onClick={()=>{
                  setVerificationMode('code');
                  setError('');
                }}
                className={`flex-1 rounded-xl px-4 py-3 text-[10px] font-black uppercase tracking-widest ${
                  verificationMode==='code'
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400'
                }`}
              >
                Enter Code
              </button>
            </div>

            {verificationMode==='qr' ? (
              <div className="mt-4 space-y-4">
                <button
                  type="button"
                  onClick={()=>setShowScanner(true)}
                  className="flex w-full items-center justify-center rounded-2xl bg-indigo-600 px-5 py-4 text-xs font-black uppercase tracking-widest text-white"
                >
                  <SafeIcon icon={FiCamera} className="mr-2" />
                  Scan Buyer QR Code
                </button>

                <textarea
                  value={qrPayload}
                  onChange={(event)=>setQrPayload(event.target.value)}
                  rows="3"
                  placeholder="The scanned signed payload appears here"
                  className="w-full rounded-2xl border border-transparent bg-white px-4 py-3 text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                />

                <button
                  type="button"
                  onClick={redeem}
                  disabled={!canVerify}
                  className={`flex w-full items-center justify-center rounded-2xl px-5 py-4 text-xs font-black uppercase tracking-widest transition-all ${
                    canVerify
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'cursor-not-allowed bg-slate-300 text-slate-500'
                  }`}
                >
                  <SafeIcon
                    icon={loading ? FiRefreshCw : FiCheckCircle}
                    className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                  />
                  Verify and Complete
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <input
                  value={pickupCode}
                  onChange={(event)=>setPickupCode(
                    normalizePickupCode(event.target.value)
                  )}
                  inputMode="text"
                  maxLength="8"
                  placeholder="Enter the buyer's pickup code"
                  className="w-full rounded-2xl border border-transparent bg-white px-4 py-4 text-center text-xl font-black tracking-[0.2em] outline-none focus:ring-2 focus:ring-indigo-500"
                />

                <button
                  type="button"
                  onClick={redeem}
                  disabled={!canVerify}
                  className={`flex w-full items-center justify-center rounded-2xl px-5 py-4 text-xs font-black uppercase tracking-widest transition-all ${
                    canVerify
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'cursor-not-allowed bg-slate-300 text-slate-500'
                  }`}
                >
                  <SafeIcon
                    icon={loading ? FiRefreshCw : FiCheckCircle}
                    className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                  />
                  Verify and Complete
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={()=>setShowProblem(!showProblem)}
              className="mt-5 flex items-center text-[10px] font-black uppercase tracking-widest text-slate-500"
            >
              <SafeIcon icon={FiSend} className="mr-2" />
              Pickup Problem
            </button>

            {showProblem && (
              <form
                onSubmit={submitProblem}
                className="mt-5 space-y-3 rounded-2xl bg-white p-5"
              >
                <select
                  required
                  value={problem}
                  onChange={(event)=>setProblem(event.target.value)}
                  className="w-full rounded-xl bg-slate-50 px-4 py-3 text-xs font-bold outline-none"
                >
                  <option value="">Select a problem</option>
                  <option value="buyer_claims_not_received">
                    Buyer says order was not received
                  </option>
                  <option value="wrong_person">
                    Wrong person attempting pickup
                  </option>
                  <option value="missing_items">Missing items</option>
                  <option value="damaged_items">
                    Missing or damaged items
                  </option>
                  <option value="unable_to_verify">
                    Unable to verify pickup
                  </option>
                  <option value="expired_token">Expired token</option>
                  <option value="connectivity_problem">
                    Connectivity problem
                  </option>
                  <option value="other">Other</option>
                </select>

                <textarea
                  required
                  value={problemDescription}
                  onChange={(event)=>setProblemDescription(event.target.value)}
                  rows="3"
                  placeholder="Describe what happened"
                  className="w-full resize-none rounded-xl bg-slate-50 px-4 py-3 text-xs font-medium outline-none"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50"
                >
                  Submit Pickup Problem
                </button>
              </form>
            )}
          </>
        )}
      </motion.section>

      {showScanner && (
        <PickupQrScanner
          onScan={handleScan}
          onClose={()=>setShowScanner(false)}
        />
      )}
    </>
  );
};

const formatRemaining=(expiresAt)=>{
  const remaining=Math.max(
    0,
    new Date(expiresAt).getTime()-Date.now()
  );
  const minutes=Math.floor(remaining/60000);
  const seconds=Math.floor((remaining%60000)/1000);
  return `${minutes}:${String(seconds).padStart(2,'0')}`;
};

export default PickupConfirmationPanel;
